import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { register } from '../../store/slices/authSlice';

interface RegisterData {
  username: string;
  email: string;
  password1: string;
  password2: string;
}

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState<RegisterData>({
    username: '',
    email: '',
    password1: '',
    password2: '',
  });
  const [validated, setValidated] = useState(false);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useAppSelector((state) => state.auth);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    
    if (form.checkValidity() === false || formData.password1 !== formData.password2) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    try {
      await dispatch(register(formData)).unwrap();
      navigate('/auth/login', { state: { registered: true } });
    } catch (err) {
      // Ошибка обрабатывается в слайсе
    }
  };

  return (
    <div className="auth-page">
      <h2 className="text-center mb-4">Регистрация</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Form.Group className="mb-3" controlId="formUsername">
          <Form.Label>Имя пользователя</Form.Label>
          <Form.Control
            type="text"
            name="username"
            placeholder="Введите имя пользователя"
            value={formData.username}
            onChange={handleChange}
            required
            minLength={3}
            maxLength={150}
          />
          <Form.Control.Feedback type="invalid">
            Пожалуйста, введите имя пользователя (от 3 до 150 символов)
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formEmail">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            placeholder="Введите email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <Form.Control.Feedback type="invalid">
            Пожалуйста, введите корректный email
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formPassword1">
          <Form.Label>Пароль</Form.Label>
          <Form.Control
            type="password"
            name="password1"
            placeholder="Введите пароль"
            value={formData.password1}
            onChange={handleChange}
            required
            minLength={8}
          />
          <Form.Text className="text-muted">
            Минимум 8 символов
          </Form.Text>
          <Form.Control.Feedback type="invalid">
            Пароль должен содержать минимум 8 символов
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-4" controlId="formPassword2">
          <Form.Label>Подтверждение пароля</Form.Label>
          <Form.Control
            type="password"
            name="password2"
            placeholder="Повторите пароль"
            value={formData.password2}
            onChange={handleChange}
            required
            isInvalid={formData.password1 !== formData.password2 && formData.password2 !== ''}
          />
          <Form.Control.Feedback type="invalid">
            Пароли не совпадают
          </Form.Control.Feedback>
        </Form.Group>

        <div className="d-grid gap-2">
          <Button 
            variant="primary" 
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Регистрация...
              </>
            ) : (
              'Зарегистрироваться'
            )}
          </Button>
        </div>
      </Form>
      
      <div className="text-center mt-3">
        <p className="mb-0">
          Уже есть аккаунт?{' '}
          <Link to="/auth/login" className="text-decoration-none">
            Войдите
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
